/**
 * @ngdoc services
 * @memberof mercury.modules.reports
 * @name ReportPosStaff
 * @description Data access service for POS Staff data
 */
angular.module('mercury.modules.reports.staff')
.factory('ReportPosStaff', ["$q", "$resource", "userinfo", "settings", function($q, $resource, userinfo, settings) {
  var PosStaff = $resource(settings.mercHost + '/pos/staff');

  var PosStaffSales = $resource(settings.mercHost + '/pos/staff/sales');
  var PosStaffSalesByStaff = $resource(settings.mercHost + '/pos/staff/sales/:posIdentifier', {posIdentifier: '@posIdentifier'});
  var PosStaffOrders = $resource(settings.mercHost + '/pos/staff/orders');
  var PosStaffOrdersByStaff = $resource(settings.mercHost + '/pos/staff/orders/:posIdentifier', {posIdentifier: '@posIdentifier'});
  var PosStaffVoids = $resource(settings.apiHost + '/r/PosStaffVoids');
  var PosStaffItemOrders = $resource(settings.mercHost + '/pos/staff/item-orders');
  var PosStaffDiscounts = $resource(settings.mercHost + '/pos/staff/discounts');
  var PosStaffSurcharges = $resource(settings.mercHost + '/pos/staff/surcharges');
  var PosStaffTables = $resource(settings.apiHost + '/r/PosStaffTables');

  var posStaffItemGroupSales = function() {
    if (userinfo.companyPos() === 'Cloudpos') {
      return $resource(settings.mercHost + '/PosStaffItemGroupSales');
    }
    return $resource(settings.apiHost + '/r/PosStaffItemSales');
  };

  var posStaffItemGroups = function() {
    if (userinfo.companyPos() === 'Cloudpos') {
      return $resource(settings.mercHost + '/PosStaffItemGroupOrders');
    }
    return $resource(settings.apiHost + '/r/PosStaffItemGroupOrders');
  };

  var posStaffItemSales = function() {
    if (userinfo.companyPos() === 'Cloudpos') {
      return $resource(settings.mercHost + '/PosStaffItemSales');
    }
    return $resource(settings.apiHost + '/r/PosStaffItemGroupSales');
  };

  /**
   * Get all Unlinked POS Staff and exclude Deleted staff.
   *
   * @memberof ReportPosStaff
   * @return {Object} promise
   */
  function getAllActiveUnlinked() {
    if (userinfo.companyPos()) {
      return PosStaff.query({'includeDeleted': false}).$promise;
    } else {
      return $q.when([]);
    }
  }

  /**
   * Get all Unlinked POS Staff, even the deleted ones.
   *
   * @memberof ReportPosStaff
   * @return {Object} promise
   */
  function getAllUnlinked() {
    if (userinfo.companyPos()) {
      return PosStaff.query({'includeDeleted': true}).$promise;
    } else {
      return $q.when([]);
    }
  }

  /**
   * Get all sales for a report period, grouped by staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getSales(start, end) {
    return PosStaffSales.query({start: start, end: end}).$promise;
  }

  /**
   * Get all orders for a report period, grouped by staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getOrders(start, end) {
    return PosStaffOrders.query({start: start, end: end}).$promise;
  }

  /**
   * Get top void reasons for a report period, grouped by staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getVoids(start, end) {
    return PosStaffVoids.query({start: start, end: end}).$promise;
  }

  /**
   * Get voids for a report period for a staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getVoidsForStaff(start, end, posIdentifier) {
    return PosStaffVoids.query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top items orders for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemOrders(start, end) {
    return PosStaffItemOrders.query({start: start, end: end}).$promise;
  }

  /**
   * Get the items ordered for a staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemOrdersForStaff(start, end, posIdentifier) {
    return PosStaffItemOrders.query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top items sold for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemSales(start, end) {
    return posStaffItemSales().query({start: start, end: end}).$promise;
  }

  /**
   * Get the items sold for a staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemSalesForStaff(start, end, posIdentifier) {
    return posStaffItemSales().query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top item groups sold for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemGroups(start, end) {
    return posStaffItemGroups().query({start: start, end: end}).$promise;
  }

  /**
   * Get the top item groups sold for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemGroupsForStaff(start, end, posIdentifier) {
    return posStaffItemGroups().query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top item groups sold for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getItemGroupSalesForStaff(start, end, posIdentifier) {
    return posStaffItemGroupSales().query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top discount types for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getDiscounts(start, end) {
    return PosStaffDiscounts.query({start: start, end: end}).$promise;
  }

  /**
   * Get the top discount types for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getDiscountsForStaff(start, end, posIdentifier) {
    return PosStaffDiscounts.query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get the top surcharge types for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getSurcharges(start, end) {
    return PosStaffSurcharges.query({start: start, end: end}).$promise;
  }

  /**
   * Get the top surcharge types for each staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getSurchargesForStaff(start, end, posIdentifier) {
    return PosStaffSurcharges.query({start: start, end: end, posIdentifier: posIdentifier}).$promise;
  }

  /**
   * Get all sales for a report period and staff member, grouped by interval.
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @param  {String} interval report interval
   * @param {String} staffId Id of staff member to get sales for
   * @return {Object} promise
   */
  function getSalesByStaff(start, end, interval, staffId) {
    return PosStaffSalesByStaff.query({start: start, end: end, interval: interval, posIdentifier: staffId}).$promise;
  }

  /**
   * Get all sales for a report period and staff member, grouped by interval.
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @param  {String} interval report interval
   * @param {String} staffId Id of staff member to get sales for
   * @return {Object} promise
   */
  function getOrdersByStaff(start, end, interval, staffId) {
    return PosStaffOrdersByStaff.query({start: start, end: end, interval: interval, posIdentifier: staffId}).$promise;
  }

  /**
   * Get tables for a report period, grouped by staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @return {Object} promise
   */
  function getTables(start, end) {
    return PosStaffTables.query({start: start, end: end}).$promise;
  }

  /**
   * Get tables for a report period for a staff member
   *
   * @memberof ReportPosStaff
   * @param  {String} start start of report period
   * @param  {String} end end of report period
   * @param  {String} interval report interval
   * @param {String} staffId Id of staff member to get sales for
   * @return {Object} promise
   */
  function getTablesByStaff(start, end, interval, staffId) {
    return PosStaffTables.query({start: start, end: end, interval: interval, posIdentifier: staffId}).$promise;
  }

  return {
    getAllActiveUnlinked: getAllActiveUnlinked,
    getAllUnlinked: getAllUnlinked,
    getSales: getSales,
    getOrders: getOrders,
    getVoids: getVoids,
    getVoidsForStaff: getVoidsForStaff,
    getItemOrders: getItemOrders,
    getItemOrdersForStaff: getItemOrdersForStaff,
    getItemSales: getItemSales,
    getItemSalesForStaff: getItemSalesForStaff,
    getItemGroups: getItemGroups,
    getItemGroupsForStaff: getItemGroupsForStaff,
    getItemGroupSalesForStaff: getItemGroupSalesForStaff,
    getDiscounts: getDiscounts,
    getDiscountsForStaff: getDiscountsForStaff,
    getSurcharges: getSurcharges,
    getSurchargesForStaff: getSurchargesForStaff,
    getSalesByStaff: getSalesByStaff,
    getOrdersByStaff: getOrdersByStaff,
    getTables: getTables,
    getTablesByStaff: getTablesByStaff
  };
}]);
